import { useState } from "react";
import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { Button } from "@/components/ui/button";
import { useAskNiq } from "@/contexts/AskNiqContext";
import { useAuth } from "@/contexts/AuthContext";
import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";

const INTENSITY_OPTIONS = [
  { key: "mild", label: "Mild", value: 0.5 },
  { key: "moderate", label: "Moderate", value: 1.0 },
  { key: "severe", label: "Severe", value: 2.0 },
];

const CATEGORY_LABEL = { equipment: "Equipment", utility: "Utility" };

export default function Simulation() {
  const scenarios = usePolling(() => api.get("/simulation/scenarios").then((r) => r.data), 20000);
  const state = usePolling(() => api.get("/simulation/state").then((r) => r.data), 4000);
  const [busy, setBusy] = useState(false);
  const [intensityByKey, setIntensityByKey] = useState({});
  const askniq = useAskNiq();
  const { canWrite } = useAuth();

  const inject = async (s) => {
    setBusy(true);
    const intensity = intensityByKey[s.scenario_key] ?? 1.0;
    try {
      await api.post("/simulation/inject", { scenario_key: s.scenario_key, intensity });
      askniq?.requestAsk(
        `A fault has just been injected: ${s.label} (root cause: ${s.root_cause}). What is the impact on current plant state, and what action should be taken to resolve it?`,
        { label: s.label, root_cause: s.root_cause, scenario_key: s.scenario_key }
      );
    } finally {
      setBusy(false);
    }
  };
  const clear = async () => {
    setBusy(true);
    try { await api.post("/simulation/clear"); } finally { setBusy(false); }
  };

  const activeKeys = new Set((state.data?.active_scenarios || []).map((s) => s.scenario_key));

  return (
    <div className="space-y-6" data-testid="simulation-page">
      <h1 className="text-2xl font-bold font-heading text-[var(--oim-text)]">Simulation</h1>
      <ReadOnlyNotice>Fault injection is disabled for read-only accounts.</ReadOnlyNotice>

      {state.data?.active_scenarios?.length > 0 && (
        <div className="rounded-xl border p-3 text-xs" style={{ borderColor: "#FF6B00", backgroundColor: "rgba(255,107,0,0.08)", color: "#FF6B00" }} data-testid="scenario-active-injection-banner">
          Injected conditions: {state.data.active_scenarios.map((s) => `${s.label} (${s.intensity >= 2 ? "severe" : s.intensity <= 0.5 ? "mild" : "moderate"})`).join(", ")}
        </div>
      )}

      <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
        <div className="text-sm font-heading font-bold text-[var(--oim-text)] mb-3">Scenario Library</div>
        {!scenarios.data ? (
          <div className="text-sm text-[var(--oim-text-muted)]" data-testid="simulation-loading">Loading…</div>
        ) : (
          <div className="space-y-3">
            {scenarios.data.map((s) => {
              const selected = intensityByKey[s.scenario_key] ?? 1.0;
              return (
                <div key={s.scenario_key} className="flex items-center justify-between text-xs flex-wrap gap-2 pb-2 border-b" style={{ borderColor: "var(--oim-border)" }} data-testid={`scenario-row-${s.scenario_key}`}>
                  <div>
                    <span className="text-[var(--oim-text-secondary)] font-heading">{s.label}</span>
                    <span className="text-[10px] text-[var(--oim-text-dim)] ml-2 uppercase">{s.profile}</span>
                    {s.category && (
                      <span className="text-[9px] font-heading uppercase px-1.5 py-0.5 rounded-full border ml-2" style={{ borderColor: "var(--oim-border)", color: "var(--oim-text-dim)" }}>
                        {CATEGORY_LABEL[s.category]}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 rounded-full border p-0.5" style={{ borderColor: "var(--oim-border)" }} data-testid={`scenario-intensity-${s.scenario_key}`}>
                      {INTENSITY_OPTIONS.map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          disabled={activeKeys.has(s.scenario_key)}
                          onClick={() => setIntensityByKey((m) => ({ ...m, [s.scenario_key]: opt.value }))}
                          className="text-[9px] font-heading uppercase px-2 py-0.5 rounded-full transition-colors"
                          style={{
                            backgroundColor: selected === opt.value ? "var(--oim-surface-softer)" : "transparent",
                            color: selected === opt.value ? "var(--oim-text)" : "var(--oim-text-dim)",
                          }}
                          data-testid={`scenario-intensity-${s.scenario_key}-${opt.key}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <Button
                      size="sm" disabled={!canWrite || busy || activeKeys.has(s.scenario_key)}
                      onClick={() => inject(s)}
                      title={canWrite ? undefined : "Read-only account"}
                      className="bg-[#FF6B00] text-black hover:bg-[#e05f00] rounded-full text-xs"
                      data-testid={`scenario-inject-${s.scenario_key}-button`}
                    >
                      {activeKeys.has(s.scenario_key) ? "Active" : "Inject"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <Button size="sm" variant="outline" className="mt-4 border-[var(--oim-border)] text-[var(--oim-text)] text-xs" onClick={clear} disabled={!canWrite || busy} title={canWrite ? undefined : "Read-only account"} data-testid="simulation-clear-button">
          Clear all injections
        </Button>
      </div>

      <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
        <div className="text-sm font-heading font-bold text-[var(--oim-text)] mb-3">Simulated Clock</div>
        <div className="font-mono-data text-xs text-[var(--oim-text-muted)]">Elapsed: {state.data?.elapsed_minutes} sim-minutes</div>
      </div>
    </div>
  );
}
